import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import proxy from '../api/axios';
import useCachedQuery from '../hooks/useCachedQuery';
import {
  fetchContestsList,
  invalidateQuery,
  queryKeys,
  STALE_TIMES,
} from '../api/queries';
import { showAuth } from '../redux/authSlice';
import { formatDateTime } from '../common/utils';

const ACCENT = '#137274';

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const splitMs = (ms) => {
  const total = Math.max(0, ms);
  return {
    days: Math.floor(total / (1000 * 60 * 60 * 24)),
    hours: Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((total % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((total % (1000 * 60)) / 1000),
  };
};

const STATUS_META = {
  Upcoming: { color: '#2563eb', label: 'Upcoming' },
  'In Progress': { color: '#1a7f37', label: 'Live' },
  Completed: { color: '#8b949e', label: 'Completed' },
  Unknown: { color: '#8b949e', label: 'Unknown' },
};

const TimeCell = ({ value, label, compact, isDark }) => (
  <Box
    sx={{
      minWidth: compact ? 44 : 56,
      px: compact ? 0.6 : 1,
      py: compact ? 0.55 : 0.75,
      borderRadius: 1.5,
      textAlign: 'center',
      bgcolor: isDark ? alpha('#fff', 0.08) : alpha(ACCENT, 0.08),
      border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha(ACCENT, 0.16)}`,
    }}
  >
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: compact ? '0.95rem' : '1.15rem',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
        color: isDark ? '#e8f5f5' : '#0b4f51',
      }}
    >
      {pad(value)}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.58rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: isDark ? alpha('#fff', 0.5) : alpha('#0b4f51', 0.65),
        mt: 0.15,
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ContestsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const { data: contestsData, loading: contestsLoading, refetch: refetchContests } = useCachedQuery(
    queryKeys.contests,
    fetchContestsList,
    { staleTime: STALE_TIMES.contests },
  );
  const contests = Array.isArray(contestsData) ? contestsData : [];
  const loading = contestsLoading && !contestsData;
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingContest, setPendingContest] = useState(null);
  const [joining, setJoining] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  useEffect(() => {
    const needsFineTick = contests.some((c) => {
      const start = new Date(c.startTime).getTime();
      const end = new Date(c.endTime).getTime();
      // Upcoming or live — countdowns need 1s updates
      return end >= Date.now() - 1000;
    });
    const intervalMs = needsFineTick ? 1000 : 15000;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [contests]);

  const getContestStatus = useCallback(
    (contest, t = now) => {
      if (t < new Date(contest.startTime).getTime()) return 'Upcoming';
      if (
        new Date(contest.startTime).getTime() <= t &&
        new Date(contest.endTime).getTime() >= t &&
        contest.status !== 'Already Submitted' &&
        contest.status !== 'Time Expired'
      ) {
        return 'In Progress';
      }
      if (
        new Date(contest.endTime).getTime() < t ||
        contest.status === 'Already Submitted' ||
        contest.status === 'Time Expired'
      ) {
        return 'Completed';
      }
      return 'Unknown';
    },
    [now],
  );

  const enriched = useMemo(() => {
    return contests.map((contest) => {
      const status = getContestStatus(contest, now);
      const remainingMs =
        status === 'Upcoming'
          ? new Date(contest.startTime).getTime() - now
          : status === 'In Progress'
            ? new Date(contest.endTime).getTime() - now
            : 0;
      return { ...contest, status, remainingMs };
    });
  }, [contests, now, getContestStatus]);

  const counts = useMemo(() => {
    let upcoming = 0;
    let live = 0;
    let completed = 0;
    for (const c of enriched) {
      if (c.status === 'Upcoming') upcoming += 1;
      else if (c.status === 'In Progress') live += 1;
      else if (c.status === 'Completed') completed += 1;
    }
    return {
      all: enriched.length,
      upcoming,
      live,
      completed,
    };
  }, [enriched]);

  const filtered = useMemo(() => {
    let list = [...enriched];
    if (filter === 'upcoming') list = list.filter((c) => c.status === 'Upcoming');
    if (filter === 'live') list = list.filter((c) => c.status === 'In Progress');
    if (filter === 'completed') list = list.filter((c) => c.status === 'Completed');

    list.sort((a, b) => {
      const rank = { 'In Progress': 0, Upcoming: 1, Completed: 2, Unknown: 3 };
      const ra = rank[a.status] ?? 9;
      const rb = rank[b.status] ?? 9;
      if (ra !== rb) return ra - rb;
      return new Date(b.startTime) - new Date(a.startTime);
    });
    return list;
  }, [enriched, filter]);

  const featured =
    enriched.find((c) => c.status === 'In Progress') ||
    enriched
      .filter((c) => c.status === 'Upcoming')
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))[0] ||
    null;

  const handleContestClick = (contestId) => {
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    navigate(`/contests/details/${contestId}`);
  };

  const openStartConfirm = (contest, e) => {
    e?.stopPropagation?.();
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    setPendingContest(contest);
    setConfirmOpen(true);
  };

  const confirmStart = async () => {
    if (!pendingContest) return;
    setJoining(true);
    const contestId = pendingContest.id;
    const isResume = Boolean(pendingContest.timeRemaining);
    try {
      if (!isResume) {
        await proxy.post(`/contests/join`, { id: contestId });
      }
      toast.success(`Contest ${isResume ? 'resumed' : 'started'} successfully!`);
      invalidateQuery(queryKeys.contests);
      refetchContests();
      setConfirmOpen(false);
      setPendingContest(null);
      navigate(`/contests/${contestId}/live`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to start the contest. Please try again.',
      );
    } finally {
      setJoining(false);
    }
  };

  const handleViewResults = (contestId, e) => {
    e?.stopPropagation?.();
    if (!isLoggedIn) {
      dispatch(showAuth());
      return;
    }
    navigate(`/contests/${contestId}/result`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          background: pageBg,
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} size={28} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading contests…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>Contests | CrafterHack</title>
        <meta
          name="description"
          content="Compete in timed coding contests on CrafterHack — climb the leaderboard and sharpen under pressure."
        />
        <link rel="canonical" href="https://crafterhack.com/contests" />
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 3.5, md: 5 },
          pb: { xs: 3, md: 4 },
          borderBottom: `1px solid ${border}`,
          background: isDark
            ? `radial-gradient(900px 400px at 12% -20%, ${alpha(ACCENT, 0.35)}, transparent 55%), radial-gradient(700px 300px at 90% 0%, ${alpha('#d97706', 0.15)}, transparent 50%), #0f1419`
            : `radial-gradient(900px 400px at 10% -30%, ${alpha(ACCENT, 0.18)}, transparent 55%), radial-gradient(700px 300px at 95% 0%, ${alpha('#d97706', 0.1)}, transparent 50%), linear-gradient(180deg, #eef7f8 0%, #f8fafb 100%)`,
        }}
      >
        <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#d97706', isDark ? 0.25 : 0.14),
                  color: '#d97706',
                }}
              >
                <EmojiEventsOutlinedIcon />
              </Box>
              <Chip
                size="small"
                icon={<BoltOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                label={`${counts.live} live · ${counts.upcoming} upcoming`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
                  color: ACCENT,
                  '& .MuiChip-icon': { color: ACCENT },
                }}
              />
            </Box>

            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.75rem', sm: '2.15rem' },
                letterSpacing: '-0.035em',
                mb: 0.75,
              }}
            >
              Coding Contests
            </Typography>
            <Typography
              sx={{ color: muted, maxWidth: 520, mb: 0, fontSize: '0.98rem', lineHeight: 1.55 }}
            >
              Timed rounds to test speed, accuracy, and nerves. Jump into a live contest or set a
              reminder for the next one.
            </Typography>
          </motion.div>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        {/* Featured */}
        {featured && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.35 }}
          >
            <Box
              onClick={() => handleContestClick(featured.id)}
              sx={{
                mb: 3.5,
                p: { xs: 2.25, sm: 3 },
                borderRadius: 3,
                cursor: 'pointer',
                border: `1px solid ${
                  featured.status === 'In Progress'
                    ? alpha('#1a7f37', 0.35)
                    : alpha(ACCENT, 0.25)
                }`,
                background:
                  featured.status === 'In Progress'
                    ? isDark
                      ? `linear-gradient(120deg, ${alpha('#1a7f37', 0.25)}, ${surface})`
                      : `linear-gradient(120deg, ${alpha('#1a7f37', 0.12)}, #fff)`
                    : isDark
                      ? `linear-gradient(120deg, ${alpha(ACCENT, 0.28)}, ${surface})`
                      : `linear-gradient(120deg, ${alpha(ACCENT, 0.12)}, #fff)`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? 'none'
                    : `0 16px 40px ${alpha(ACCENT, 0.12)}`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Chip
                    size="small"
                    label={
                      featured.status === 'In Progress' ? 'Happening now' : 'Next up'
                    }
                    sx={{
                      mb: 1.25,
                      fontWeight: 800,
                      height: 24,
                      bgcolor:
                        featured.status === 'In Progress'
                          ? alpha('#1a7f37', isDark ? 0.3 : 0.15)
                          : alpha(ACCENT, isDark ? 0.25 : 0.12),
                      color: featured.status === 'In Progress' ? '#1a7f37' : ACCENT,
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      letterSpacing: '-0.03em',
                      mb: 1,
                    }}
                  >
                    {featured.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: muted, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
                      {formatDateTime(featured.startTime).date} ·{' '}
                      {formatDateTime(featured.startTime).time}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: muted, display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <TimerOutlinedIcon sx={{ fontSize: 16 }} />
                      {featured.duration} mins
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5 }}>
                  {featured.status === 'Upcoming' && featured.remainingMs > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {Object.entries(splitMs(featured.remainingMs)).map(([k, v]) => (
                        <TimeCell
                          key={k}
                          value={v}
                          label={k.slice(0, 3)}
                          isDark={isDark}
                        />
                      ))}
                    </Box>
                  )}
                  {featured.status === 'In Progress' && (
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={(e) => openStartConfirm(featured, e)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: '#1a7f37',
                        '&:hover': { bgcolor: '#15632c' },
                      }}
                    >
                      {featured.timeRemaining ? 'Resume contest' : 'Enter live'}
                    </Button>
                  )}
                  {featured.status === 'Upcoming' && (
                    <Button
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContestClick(featured.id);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: ACCENT,
                        '&:hover': { bgcolor: '#0f5c5e' },
                      }}
                    >
                      View details
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
            All contests
          </Typography>
          <Tabs
            value={filter}
            onChange={(_e, v) => setFilter(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                minHeight: 40,
                py: 0,
                px: 1.5,
                fontSize: '0.85rem',
              },
              '& .Mui-selected': { color: `${ACCENT} !important` },
              '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: 2 },
            }}
          >
            <Tab value="all" label={`All (${counts.all})`} />
            <Tab value="live" label={`Live (${counts.live})`} />
            <Tab value="upcoming" label={`Upcoming (${counts.upcoming})`} />
            <Tab value="completed" label={`Done (${counts.completed})`} />
          </Tabs>
        </Box>

        {filtered.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              borderRadius: 3,
              border: `1px dashed ${border}`,
              bgcolor: surface,
            }}
          >
            <ScheduleOutlinedIcon sx={{ fontSize: 40, color: muted, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No contests here</Typography>
            <Typography variant="body2" sx={{ color: muted }}>
              Try another filter — or check back soon for the next round.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 1.75,
            }}
          >
            {filtered.map((contest, i) => {
              const meta = STATUS_META[contest.status] || STATUS_META.Unknown;
              const start = formatDateTime(contest.startTime);
              const end = formatDateTime(contest.endTime);
              const countdown = splitMs(contest.remainingMs);

              return (
                <motion.div
                  key={contest.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.24), duration: 0.3 }}
                >
                  <Box
                    onClick={() => handleContestClick(contest.id)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2.5,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        borderColor: alpha(ACCENT, 0.45),
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                          ? 'none'
                          : `0 12px 32px ${alpha(ACCENT, 0.1)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        background:
                          contest.status === 'In Progress'
                            ? `linear-gradient(90deg, ${alpha('#1a7f37', isDark ? 0.35 : 0.18)}, transparent)`
                            : contest.status === 'Upcoming'
                              ? `linear-gradient(90deg, ${alpha(ACCENT, isDark ? 0.3 : 0.14)}, transparent)`
                              : isDark
                                ? alpha('#fff', 0.03)
                                : alpha('#000', 0.02),
                        borderBottom: `1px solid ${border}`,
                      }}
                    >
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          bgcolor: alpha(meta.color, isDark ? 0.25 : 0.12),
                          color: meta.color,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: muted, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.4 }}
                      >
                        <TimerOutlinedIcon sx={{ fontSize: 14 }} />
                        {contest.duration}m
                      </Typography>
                    </Box>

                    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1rem',
                          letterSpacing: '-0.02em',
                          mb: 1.5,
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '2.6em',
                        }}
                      >
                        {contest.title}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: muted, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8rem' }}
                        >
                          <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: ACCENT }} />
                          Starts {start.date} · {start.time}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: muted, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8rem' }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: muted }} />
                          Ends {end.date} · {end.time}
                        </Typography>
                      </Box>

                      {contest.status === 'Upcoming' && contest.remainingMs > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.6, mb: 2 }}>
                          <TimeCell value={countdown.days} label="Days" compact isDark={isDark} />
                          <TimeCell value={countdown.hours} label="Hrs" compact isDark={isDark} />
                          <TimeCell value={countdown.minutes} label="Min" compact isDark={isDark} />
                          <TimeCell value={countdown.seconds} label="Sec" compact isDark={isDark} />
                        </Box>
                      )}

                      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {contest.status === 'In Progress' && (
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<PlayArrowRoundedIcon />}
                            onClick={(e) => openStartConfirm(contest, e)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 800,
                              bgcolor: '#1a7f37',
                              '&:hover': { bgcolor: '#15632c' },
                            }}
                          >
                            {contest.timeRemaining ? 'Resume' : 'Start now'}
                          </Button>
                        )}
                        {contest.status === 'Upcoming' && (
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContestClick(contest.id);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderColor: border,
                              color: 'inherit',
                            }}
                          >
                            View details
                          </Button>
                        )}
                        {contest.status === 'Completed' && (
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<LeaderboardOutlinedIcon />}
                            onClick={(e) => handleViewResults(contest.id, e)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderColor: border,
                              color: 'inherit',
                            }}
                          >
                            View results
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !joining && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          {pendingContest?.timeRemaining ? 'Resume contest?' : 'Start contest?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55 }}>
            {pendingContest?.timeRemaining
              ? `Resume “${pendingContest?.title}” where you left off?`
              : `You’re about to enter “${pendingContest?.title}”. The timer starts when you join — ready?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={joining}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmStart}
            disabled={joining}
            startIcon={
              joining ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            {joining
              ? 'Joining…'
              : pendingContest?.timeRemaining
                ? 'Resume'
                : 'Start now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContestsPage;
